import { Save as SaveIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Grid,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import type { FormikProps } from 'formik';
import type { Position } from 'geojson';
import type { FC } from 'react';
import type { Contract } from 'validata';
import { isDate, isNumber, isString, maybeString, maybeTuple } from 'validata';
import { FormikAutocomplete } from '../../../components/formik-fields/autocomplete.js';
import { FormikLocationField } from '../../../components/formik-fields/location-field.js';
import { FormikLocationMapField } from '../../../components/formik-fields/location-map-field.js';
import { FormikSlider } from '../../../components/formik-fields/slider.js';
import { FormikTextField } from '../../../components/formik-fields/text-field.js';
import { IncidentsLayer } from '../../../components/incidents/layer.js';
import {
  type WithIncidentProps,
  withIncidents,
} from '../../../components/incidents/with-incidents.js';
import { BottomActionBar } from '../../../components/page-layout/bottom-action-bar.js';
import { Clock } from './clock.js';
import { peopleMarks } from './marks.js';
import type { VehicleCheckProps } from './vehicle-check.controller.js';
import { VehicleCheck } from './vehicle-check.controller.js';

export interface AddIncidentForm {
  address?: string;
  locationNotes?: string;
  people: number;
  peopleDetails: string;
  position?: Position;
  what?: string;
  whatDetails: string;
  when: Date;
}

export const addIncidentFormContract: Contract<AddIncidentForm> = {
  address: maybeString(),
  locationNotes: maybeString(),
  people: isNumber(),
  peopleDetails: isString(),
  position: maybeTuple([isNumber(), isNumber()]),
  what: isString(),
  whatDetails: isString(),
  when: isDate(),
};

export const incidentCategories = [
  'Site check',
  'Vehicle check',
  'Requested checkup/visit',
  'Rubbish',
  'Accident',
  'Alarm activated',
  'Dodgy people looking shifty',
  'Disorderly conduct',
  'Fire',
  'Graffiti',
  'Medical assistance',
  'Other',
];

interface Props extends VehicleCheckProps {
  getCurrentPosition: () => void;
  isGeolocationAvailable: boolean;
  isGeolocationEnabled: boolean;
  onCancel: () => void;
  positionLoading: boolean;
  recordPatrolForm: FormikProps<AddIncidentForm>;
  smallScreen: boolean;
}

export const AddIncidentView: FC<Props> = withIncidents((props) => (
  <AddIncidentViewImpl {...props} />
));

export const AddIncidentViewImpl: FC<Props & WithIncidentProps> = ({
  recordPatrolForm,
  getCurrentPosition,
  isGeolocationAvailable,
  isGeolocationEnabled,
  onCancel,
  onClick,
  onUnselect,
  points,
  positionLoading,
  selected: incident,
  smallScreen,
  checkVehicle,
  removeVehicle,
  vehicles,
}) => (
  <Box
    component="form"
    onReset={recordPatrolForm.handleReset}
    onSubmit={recordPatrolForm.handleSubmit}
    sx={{
      pb: 'calc(96px + env(safe-area-inset-bottom, 0px))',
    }}
  >
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant={smallScreen ? 'h6' : 'h4'}>
              Record Activity
            </Typography>
            <Button
              onClick={onCancel}
              sx={{ alignSelf: 'end' }}
              variant="contained"
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} xs={12}>
        <Card sx={{ height: '100%' }}>
          <CardHeader subheader="Detailed observations" title="What" />
          <CardContent>
            <Stack direction="column" flexGrow={1} spacing={3}>
              <FormikAutocomplete
                disablePortal
                formik={recordPatrolForm}
                id="what"
                label="Category"
                options={incidentCategories}
              />
              <FormikTextField
                formik={recordPatrolForm}
                id="whatDetails"
                label="Details"
                placeholder="More info about the incident or activity"
              />
              <VehicleCheck
                checkVehicle={checkVehicle}
                removeVehicle={removeVehicle}
                vehicles={vehicles}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} xs={12}>
        <Card sx={{ height: '100%' }}>
          <CardHeader subheader="People present" title="Who" />
          <CardContent>
            <Stack direction="column" flexGrow={1} spacing={3}>
              <Box sx={{ px: 2 }}>
                <FormikSlider
                  aria-labelledby="people-slider-label"
                  formik={recordPatrolForm}
                  id="people"
                  marks={peopleMarks}
                  max={5}
                  min={0}
                  step={1}
                />
              </Box>
              <FormikTextField
                formik={recordPatrolForm}
                id="peopleDetails"
                label="Details"
                placeholder="More info about people present"
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} xs={12}>
        <Card sx={{ height: '100%' }}>
          <CardHeader
            subheader="Location of activity or incident"
            title="Where"
          />
          <CardContent>
            <Stack spacing={3}>
              {(!isGeolocationEnabled || !isGeolocationAvailable) &&
              !recordPatrolForm.values.position ? (
                <FormikLocationField
                  formik={recordPatrolForm}
                  id="position"
                  label="Location"
                />
              ) : null}
              {isGeolocationAvailable && isGeolocationEnabled ? (
                <LoadingButton
                  loading={positionLoading}
                  onClick={() => getCurrentPosition()}
                >
                  Use Current Location
                </LoadingButton>
              ) : null}
              <Collapse in={!!recordPatrolForm.values.position}>
                <Stack spacing={3}>
                  <Stack
                    sx={{
                      '& .popup': {
                        '& .mapboxgl-popup-content': {
                          padding: '16px',
                        },
                        color: 'black',
                      },
                    }}
                  >
                    <Typography color="text.secondary" variant="caption">
                      {recordPatrolForm.values.position
                        ? `${recordPatrolForm.values.position[1].toFixed(
                            5,
                          )}, ${recordPatrolForm.values.position[0].toFixed(5)}`
                        : null}
                    </Typography>
                    <FormikLocationMapField
                      formik={recordPatrolForm}
                      height={200}
                      id="position"
                    >
                      <IncidentsLayer
                        onClick={onClick}
                        onUnselect={onUnselect}
                        points={points}
                        selected={incident}
                      />
                    </FormikLocationMapField>
                    <Typography
                      color="text.secondary"
                      fontStyle="italic"
                      variant="caption"
                    >
                      Drag the marker on the map to adjust the location
                    </Typography>
                  </Stack>
                  <FormikTextField
                    disabled
                    formik={recordPatrolForm}
                    id="address"
                    label="Address"
                    multiline
                    rows={2}
                  />
                  <FormikTextField
                    formik={recordPatrolForm}
                    id="locationNotes"
                    label="Location Notes"
                  />
                </Stack>
              </Collapse>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} xs={12}>
        <Card sx={{ height: '100%' }}>
          <CardHeader subheader="The time that it occurred" title="When" />
          <CardContent>
            <Typography variant="h4">
              <Clock />
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <BottomActionBar>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { sm: 'flex-end', xs: 'stretch' },
            }}
          >
            <LoadingButton
              color="primary"
              fullWidth={!smallScreen ? false : true}
              loading={recordPatrolForm.isSubmitting}
              size="large"
              startIcon={<SaveIcon />}
              sx={{ minWidth: { sm: 200, xs: 160 } }}
              type="submit"
              variant="contained"
            >
              Save
            </LoadingButton>
          </Box>
        </BottomActionBar>
      </Grid>
    </Grid>
  </Box>
);
